import { useEffect, useState, type ReactNode } from 'react';

import type { RuntimeGraphManifest } from '@entangled-vega/shared';

import { OPEN_SOURCE_NOTICES } from '../data/open-source-notices.js';
import { loadProductionManifest } from '../data/runtime-loader.js';
import { SiteHeader } from './SiteHeader.js';

type StaticPageProps = {
  badge: string;
  children: ReactNode;
  eyebrow: string;
  title: string;
};

function StaticPage({ badge, children, eyebrow, title }: StaticPageProps) {
  return (
    <div className="app-shell static-shell">
      <SiteHeader badge={badge} />
      <main id="main-content" className="static-page">
        <a className="static-page__back" href="/">
          ← Return to the constellation
        </a>
        <header className="static-page__hero">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
        </header>
        <div className="prose">{children}</div>
        <footer className="static-page__footer">
          <a href="/work-with-us">Work with us</a>
          <a href="/about">About</a>
          <a href="/privacy">Privacy &amp; Legal</a>
        </footer>
      </main>
    </div>
  );
}

function snapshotDateLabel(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

function SnapshotFacts() {
  const [manifest, setManifest] = useState<RuntimeGraphManifest | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void loadProductionManifest({ signal: controller.signal }).then(
      (result) => {
        if (!controller.signal.aborted) {
          setManifest(result);
        }
      },
      () => {
        if (!controller.signal.aborted) {
          setUnavailable(true);
        }
      },
    );
    return () => controller.abort();
  }, []);

  if (manifest === null) {
    return (
      <p className="snapshot-status" role="status">
        {unavailable
          ? 'Current snapshot details are temporarily unavailable. The atlas itself shows its loaded snapshot version.'
          : 'Loading current snapshot details…'}
      </p>
    );
  }

  return (
    <dl className="snapshot-facts">
      <div>
        <dt>Data snapshot</dt>
        <dd>
          <time dateTime={manifest.snapshotDate}>{snapshotDateLabel(manifest.snapshotDate)}</time>
        </dd>
      </div>
      <div>
        <dt>People</dt>
        <dd>{manifest.counts.people.toLocaleString('en-US')}</dd>
      </div>
      <div>
        <dt>Connections</dt>
        <dd>{manifest.counts.relationships.toLocaleString('en-US')}</dd>
      </div>
      <div>
        <dt>Sources</dt>
        <dd>{manifest.counts.sources.toLocaleString('en-US')} retained source records</dd>
      </div>
    </dl>
  );
}

export function AboutPage() {
  return (
    <StaticPage badge="About the atlas" eyebrow="About the atlas" title="About Entangled Vega">
      <p className="prose__lead">
        Entangled Vega is an entertainment-first 3D atlas for exploring publicly documented Personal
        and Family connections between recognizable people. It brings external relationship data
        into one interactive constellation; it does not invent relationships.
      </p>

      <nav className="about-index" aria-label="About page sections">
        <a href="#explore">How to explore</a>
        <a href="#connections">Connections and sources</a>
        <a href="#snapshot">Current snapshot</a>
        <a href="#limits">Limits and independence</a>
      </nav>

      <section id="explore" className="prose__overview">
        <h2>How to explore</h2>
        <p>
          Find a person or open the Daily connection, then select stars and relationship cards to
          walk through the constellation. Daily rotates one accepted featured path at midnight UTC;
          every revealed path opens the same source-linked Explorer state.
        </p>
        <ul>
          <li>
            Relationship filters show or hide spouses, partners, children, parents, siblings, and
            extended family in both the dossier and the constellation.
          </li>
          <li>Direct, 2 hops, and Universe change how far the surrounding web extends.</li>
          <li>
            Connect a selected person to someone else to reveal a source-linked route, or use List
            view for the same facts without the 3D scene.
          </li>
          <li>
            Saved keeps bounded favorite and recent people and completed paths on this device. It
            does not create an account or sync activity elsewhere.
          </li>
        </ul>
      </section>

      <section id="connections">
        <h2>Connections and sources</h2>
        <p>
          Every line is a people-to-people Personal or Family relationship claim. Relationship
          labels explain what the connection is; Wikidata, Reported, Rumored, and Disputed labels
          explain where the claim came from or how the source presents it. Exact source links remain
          available in each relationship’s Sources disclosure.
        </p>
        <p>
          Wikidata is the primary source. Clearly stated relationships from bounded
          English-Wikipedia relationship content may add attributed supplemental connections.
          Entangled Vega does not create lines from shared employers, film credits, organizations,
          events, or article mentions.
        </p>
      </section>

      <section id="snapshot">
        <h2>Current published snapshot</h2>
        <SnapshotFacts />
        <p>
          The atlas is a validated static snapshot, not a live copy of its sources. Upstream changes
          appear after a later accepted refresh. A missing person, relationship, or path means only
          that it is absent from this snapshot and the enabled relationship filters; it does not
          prove that no real-world connection exists.
        </p>
      </section>

      <section id="limits">
        <h2>Limits and independence</h2>
        <p>
          Public-source information can be incomplete, uneven, outdated, disputed, or wrong.
          Entangled Vega validates identities, dates, structure, and publication integrity before a
          snapshot is released, but it does not independently verify every upstream claim.
        </p>
        <p>
          The site is independent and is not affiliated with or endorsed by any person shown or
          source provider. Privacy, correction guidance, and the site’s single contact address are
          on <a href="/privacy">Privacy &amp; Legal</a>.
        </p>
        <p>
          For provenance-complete maps, story kits, or a configured presentation pilot, see{' '}
          <a href="/work-with-us">Work with us</a>. Commercial work changes presentation and
          delivery only—never factual inclusion, ranking, labels, sources, featured-path selection,
          or correction outcomes.
        </p>
      </section>
    </StaticPage>
  );
}

export function WorkWithUsPage() {
  return (
    <StaticPage
      badge="Pilot services"
      eyebrow="Work with us"
      title="Turn a sourced connection into a useful presentation"
    >
      <p className="prose__lead">
        Entangled Vega offers small, manually delivered pilots built from the same accepted Personal
        and Family graph as the public Explorer. You are buying presentation, creative service, and
        configured delivery—never facts.
      </p>

      <section>
        <h2>Three pilot offers</h2>
        <div className="commercial-offers">
          <article>
            <p className="eyebrow">Constellation artifact</p>
            <h3>Provenance-complete relationship map</h3>
            <p>
              A polished high-resolution map with canonical route, snapshot identity, relationship
              labels, source access, attribution, and a delivery-ready export.
            </p>
            <strong>$250–$750 pilot hypothesis</strong>
          </article>
          <article>
            <p className="eyebrow">Connection story kit</p>
            <h3>A sourced path told step by step</h3>
            <p>
              A compact narrative sequence with exact path meaning, dates and status, source
              disclosures, and share-ready presentation assets.
            </p>
            <strong>$750–$2,500 pilot hypothesis</strong>
            <small>Branded story hypothesis: $2,500–$7,500.</small>
          </article>
          <article>
            <p className="eyebrow">Configured embed pilot</p>
            <h3>A responsive presentation for your context</h3>
            <p>
              A manually configured preview for a publisher, educator, event, or creator. Scope,
              delivery, accessibility, and provenance are agreed before work begins.
            </p>
            <strong>$7,500+ after discovery</strong>
            <small>This is a pilot hypothesis, not a universal project floor.</small>
          </article>
        </div>
      </section>

      <section>
        <h2>See the generated demonstrations</h2>
        <div className="commercial-demos">
          <a href="/work-with-us/demos/relationship-map/">
            <strong>High-resolution relationship map</strong>
            <span>One accepted path rendered as a presentation-ready factual artifact.</span>
          </a>
          <a href="/work-with-us/demos/connection-story/">
            <strong>Multi-step connection story</strong>
            <span>One accepted featured route with relationship and source detail.</span>
          </a>
          <a href="/work-with-us/demos/configured-embed/">
            <strong>Responsive configured-embed preview</strong>
            <span>A bounded presentation preview, not a live factual service.</span>
          </a>
        </div>
        <p>
          These examples are selected deterministically during the build from accepted featured
          material. They regenerate or disappear when the accepted graph or a correction suppression
          changes. Each example links its exact artifact provenance manifest.
        </p>
      </section>

      <section>
        <h2>Independence is part of the deliverable</h2>
        <p>
          Payment cannot buy factual inclusion or removal, ranking, relationship status, source
          access, a featured path, or a correction result. Any future sponsor or presented-by
          treatment must be plainly disclosed and structurally separate from deterministic factual
          selection. No person or source shown in a pilot endorses Entangled Vega or its client.
        </p>
        <p>
          Initial evidence is limited to aggregate traffic for this commercial route and manually
          recorded qualified emails and pilot outcomes. There is no account, intake form, checkout,
          subscription, or custom interaction telemetry.
        </p>
      </section>

      <section>
        <h2>Start with an email</h2>
        <p>
          Describe the audience, intended format, deadline, and where the result will appear. Do not
          send passwords, private relationship information, or sensitive credentials.
        </p>
        <a className="commercial-contact" href="mailto:contact@entangledvega.com">
          Email contact@entangledvega.com
        </a>
        <p>
          Source, privacy, legal, and correction guidance remains on{' '}
          <a href="/privacy">Privacy &amp; Legal</a>.
        </p>
      </section>
    </StaticPage>
  );
}

export function PrivacyPage() {
  return (
    <StaticPage badge="Policy" eyebrow="Plain-language policy" title="Privacy & Legal">
      <p className="prose__lead">
        Entangled Vega is an entertainment site for exploring publicly documented Personal and
        Family connections. It does not invent relationship claims.
      </p>

      <section>
        <h2>Sources and accuracy</h2>
        <p>
          Connections come from <a href="https://www.wikidata.org/">Wikidata</a>, whose structured
          data is available under CC0, and attributed public sources. Labels and source links show
          where each claim came from. Upstream information may be incomplete, disputed, outdated, or
          wrong, and Entangled Vega does not independently verify it.
        </p>
        <p>
          The site is provided as-is for informational and entertainment purposes, without
          warranties, and is not legal advice. Entangled Vega is independent and is not endorsed by
          any person shown or source provider.
        </p>
      </section>

      <section>
        <h2>Privacy</h2>
        <p>
          No account is required. The site has no comments, advertising trackers, personalization
          server, cookies, or custom relationship telemetry. Shareable exploration state may appear
          in the URL. The optional Saved library stores only person QIDs; canonical trace endpoints,
          non-default relationship lenses, and accepted story IDs; ordered recency without
          timestamps; and one current-day challenge result. It never stores search strings, source
          URLs, dossier or source content, free text, email addresses, browser identity, or guesses
          beyond that current-day result.
        </p>
        <p>
          Saved activity stays in this browser on this device. It is not synced or sent to Entangled
          Vega. Remove an individual item, clear recents, or use the confirmed clear-all control in
          Saved. The browser's site-data controls can also erase it. The site uses local storage
          first and falls back to this tab's session storage or page memory when persistent storage
          is unavailable, with a visible notice when that happens.
        </p>
        <p>
          Cloudflare still processes ordinary request and security data and provides the same
          aggregate web analytics. Entangled Vega does not send names, QIDs, trace endpoints, story
          IDs, guesses, favorites, recent activity, or source URLs through analytics.
        </p>
      </section>

      <section>
        <h2>Commercial independence</h2>
        <p>
          Entangled Vega may sell presentation, creative service, and configured delivery. It does
          not sell factual inclusion or removal, ranking, relationship status, source access,
          featured-path selection, or correction outcomes. Any sponsor treatment must be disclosed
          separately from deterministic factual selection.
        </p>
      </section>

      <section>
        <h2>Corrections and contact</h2>
        <p>
          For Wikidata-backed information, please correct the corresponding Wikidata statement so a
          later Entangled Vega snapshot can inherit the upstream change. For site, privacy, legal,
          or source-display questions, the only public contact is{' '}
          <a href="mailto:contact@entangledvega.com">contact@entangledvega.com</a>. Do not send
          passwords or sensitive credentials.
        </p>
      </section>

      <details id="open-source-notices" className="oss-disclosure">
        <summary>Open-source notices</summary>
        <div className="oss-disclosure__body">
          <p>
            The application uses these pinned open-source runtime packages. Their authors do not
            endorse Entangled Vega.
          </p>
          <ul className="oss-notices">
            {OPEN_SOURCE_NOTICES.map((notice) => (
              <li key={notice.packageName}>
                <strong>{notice.displayName}</strong> {notice.version} —{' '}
                <a href={notice.licenseUrl}>{notice.license} License</a>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </StaticPage>
  );
}

export function NotFoundPage() {
  return (
    <StaticPage badge="404" eyebrow="404 · Uncharted space" title="That path is not here">
      <p className="prose__lead">
        The requested page does not exist. Return to the constellation to keep exploring.
      </p>
    </StaticPage>
  );
}
